import { z } from "zod";
/**
 * **The profile tier — architecture §16g A72/A73/A74, Ivy's S6 profile spec.**
 *
 * ── Why this is a second endpoint and not four more fields on `players/me` ───
 *
 * A72 is LOCKED that `GET /api/v1/players/me` **does not grow**. The profile
 * screen composes **four** reads:
 *
 * | # | Source | Carries |
 * |---|---|---|
 * | 1 | `GET /players/me` — unchanged | nickname, avatar, role, `canChangeIdentity`, `balanceCoins` |
 * | 2 | `GET /players/me/profile` — here | `{ emailMasked, nicknameChange, avatarChange }` **and nothing else** |
 * | 3 | `GET /players/me/stats` | `stats.ts` |
 * | 4 | `GET /players/me/transactions` | `transactions.ts` |
 *
 * The mask needs a join to the auth user; putting that on the app's hottest
 * endpoint would run it on every screen for a string one card reads. And the
 * four fail independently (Ivy §8): one card failing never blanks the screen.
 *
 * **No balance in 2, 3 or 4** (A72 LOCKED item 1). `balanceCoins` on source 1 is
 * the one-truth carrier and the socket is the same value's live edge; a second
 * number 300 px away is a defect even when it agrees.
 */
/**
 * Why an identity edit is refused — a **closed** enum, one COPY-SLOT per value
 * (Ivy §12 rule 3).
 *
 * **LOCKED rule 31 (§16g A73):** *no client computes identity-edit eligibility.
 * Not from `role`, not from the route, not from `canChangeIdentity`, not from a
 * stored timestamp. The server's `blockedReason` is the only input, and a value
 * outside this enum must never be synthesised.*
 *
 * Precedence is **`suspended` → `guest` → `in_game` → `cooldown`**, first match
 * wins: a guest in a room reads `guest`, because that is the durable fact and the
 * one with an action attached; a suspended account reads `suspended` ahead of all
 * of them, because it is the strongest and most truthful block.
 *
 * `suspended` is A112 / **LOCKED rule 41** (§16q): the deny-by-default suspension
 * gate refuses every identity write at `requirePlayer`, so the advertised answer
 * (`GET /players/me/profile`, an allow-listed read) must name the same fact the
 * write enforces — otherwise the Edit control says "available" while the submit
 * 403s. Per rule 31 the client renders this value; it never derives it from
 * `players.is_suspended` itself.
 *
 * `429` is **not** in this enum and never will be. Ivy §4.5's rider makes the
 * rename rate limit a *limit*, not a product rule — the UI renders them
 * differently and collapsing them would teach a player that we impose a cooldown
 * we deliberately do not have.
 */
export const IDENTITY_BLOCKED_REASONS = ["guest", "cooldown", "in_game", "suspended"];
export const identityBlockedReasonSchema = z.enum(IDENTITY_BLOCKED_REASONS);
/**
 * The avatar half can never be blocked by a cooldown (Ivy §4.5, LOCKED) — but it
 * IS blocked by `suspended` (rule 41): a suspended account changes nothing.
 */
export const AVATAR_BLOCKED_REASONS = ["guest", "in_game", "suspended"];
export const avatarBlockedReasonSchema = z.enum(AVATAR_BLOCKED_REASONS);
export const nicknameChangeEligibilitySchema = z.object({
    allowed: z.boolean(),
    blockedReason: identityBlockedReasonSchema.nullable(),
    /**
     * ISO, and **only** ever set alongside `blockedReason: "cooldown"`. Ivy §4.5:
     * the note names an absolute date, never a live countdown — a ticking timer on
     * a three-day wait is a liveness obligation for no benefit.
     *
     * `null` when `allowed` is true: rendering "you can change this again in N
     * days" for a rule we do not have is the honesty rule applied to numbers.
     */
    nextAllowedAt: z.string().nullable(),
});
/**
 * Two objects, two computations. **A nickname cooldown never implies an avatar
 * cooldown** (Ivy §4.5, LOCKED) — do not wire one boolean to both.
 */
export const avatarChangeEligibilitySchema = z.object({
    allowed: z.boolean(),
    blockedReason: avatarBlockedReasonSchema.nullable(),
});
export const identityEligibilitySchema = z.object({
    nicknameChange: nicknameChangeEligibilitySchema,
    avatarChange: avatarChangeEligibilitySchema,
});
/**
 * `GET /api/v1/players/me/profile` — **A72 source 2, and nothing else.**
 *
 * `emailMasked` is masked **server-side** and the address never ships, in any
 * payload, log or error (A72 LOCKED item 2, Ivy §7.1). Format, LOCKED: first
 * character of the local part, then **exactly four** `•` regardless of the real
 * length, then `@` and the full domain — `a••••@gmail.com`. Fixed-width bullets
 * so the mask does not leak the length, and there is **no reveal control**: a
 * mask that unmasks on tap protects nothing.
 *
 * `null` for a guest (they have no email — Ivy §7.2: the row is absent, not
 * "none") and for a registered player whose auth row cannot be resolved, which
 * is a state the screen renders as "no email row" rather than as an error.
 */
export const playerProfileDetailsSchema = z.object({
    emailMasked: z.string().nullable(),
    nicknameChange: nicknameChangeEligibilitySchema,
    avatarChange: avatarChangeEligibilitySchema,
});
/**
 * `PATCH /api/v1/players/me/identity` — **A74: one write, one transaction, one
 * response the client adopts.**
 *
 * Ivy §4.2 is LOCKED that nickname and avatar commit together: two independent
 * auto-saving controls would give the screen two success states, two error
 * states and two ways to be half-applied. At least one field must be present —
 * an empty body is a 400 from the schema, before any handler runs.
 *
 * The 200 body is the full new `PlayerProfile` (see `players.ts`), which the
 * client writes into its `players/me` cache **synchronously**; that is what makes
 * Ivy §4.6 rule 2 — one invalidation swaps the top bar's 34, the flyout's 54 and
 * the profile's 96 in the same frame — literally true.
 */
export const updateIdentityRequestSchema = z
    .object({
    /**
     * Deliberately as loose here as `updateNicknameRequestSchema` — `min(1)`,
     * `max(64)` — and for the same reason: the *rules* (length, charset,
     * screen, uniqueness) are `IdentityService.checkNickname`'s, so both rename
     * routes fail a bad name the same way, with `bad_request` + `details.rule`,
     * rather than one of them answering with a Zod validation envelope the
     * client's field states have never seen.
     *
     * `nicknameSchema` is still the shared rule: it is what the client validates
     * with and what `NICKNAME_PATTERN` re-states server-side. A rename is not a
     * second nickname pipeline (LOCKED, design spec §9: `taken` and `banned`
     * stay indistinguishable).
     */
    nickname: z.string().min(1).max(64).optional(),
    avatarId: z.string().uuid().optional(),
})
    .refine((body) => body.nickname !== undefined || body.avatarId !== undefined, {
    message: "Provide a nickname, an avatarId, or both",
});
//# sourceMappingURL=profile.js.map